import React, { createContext, useContext, useState, useEffect, } from "react";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, confirmPasswordReset, sendEmailVerification, updateProfile as firebaseUpdateProfile, signOut as firebaseSignOut, onAuthStateChanged, } from "firebase/auth";
import { auth } from "../utils/firebase";
import { createUserDocument, getUserData, updateUserRole, UserRole, } from "../utils/userRoles";
import { logAuthEvent as _logAuthEvent } from "../services/authLoggingService";
import { secureAuthService } from "../services/secureAuthService";
import { useIdleTimeout } from '../hooks/useIdleTimeout'; // Import the new hook
// Define the idle timeout duration (e.g., 15 minutes)
const IDLE_TIMEOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
// Safe wrapper for auth logging that handles Firestore permission errors
const logAuthEvent = async (data) => {
    try {
        return await _logAuthEvent(data);
    }
    catch (error) {
        console.warn("Unable to log auth event to Firestore:", error);
        // Return a mock ID to maintain compatibility
        return "mock-log-id";
    }
};
// Create the auth context
const AuthContext = createContext(null);
// Custom hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
// Convert Firebase user to our User type
const formatUser = async (firebaseUser) => {
    let userData = null;
    try {
        // Get user data from Firestore to include role information
        userData = await getUserData(firebaseUser.uid);
    }
    catch (error) {
        console.warn("Unable to fetch user data from Firestore:", error);
        // For AWS Cognito users, we don't have Firestore access
        // Default to basic user role
    }
    return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "",
        photoURL: firebaseUser.photoURL,
        emailVerified: firebaseUser.emailVerified,
        role: userData?.role || UserRole.USER,
        isAdmin: userData?.role === UserRole.ADMIN,
    };
};
// Auth provider component
export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Handle idle timeout
    useIdleTimeout({
        timeout: IDLE_TIMEOUT_DURATION,
        onIdle: () => {
            if (currentUser) {
                console.log("User idle, logging out...");
                logout(); // Automatically log out the user
            }
        },
        // Optionally, you can add an onActive callback if needed
        onActive: () => {
            if (error === "User session expired due to inactivity.") {
                setError(null);
            }
        },
    });
    // Register a new user using Firebase
    const register = async (email, password, displayName) => {
        try {
            setError(null);
            // Sign up with Firebase
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            // Set display name if provided
            if (displayName && user) {
                await firebaseUpdateProfile(user, {
                    displayName: displayName,
                });
            }
            // Create user document in Firestore with regular user role
            if (user) {
                await createUserDocument(user.uid, user.email || "", displayName || user.displayName || undefined);
                // Send email verification
                await sendEmailVerification(user);
            }
            console.log("Registration result:", user);
            // Log successful registration
            if (user) {
                await logAuthEvent({
                    userId: user.uid,
                    email: email,
                    eventType: 'register',
                    status: 'success',
                    details: `New user registered with Firebase${displayName ? ` as ${displayName}` : ''}`,
                    ipAddress: undefined
                });
                // Return registration result
                return {
                    userId: user.uid,
                    isSignUpComplete: false, // Email verification required
                    nextStep: { signUpStep: "CONFIRM_SIGN_UP" },
                };
            }
        }
        catch (err) {
            console.error("Registration error:", err);
            setError(err instanceof Error ? err.message : "Failed to register");
            if (!(err instanceof Error))
                throw new Error('An unknown error occurred');
            // Log failed registration
            await logAuthEvent({
                email: email,
                eventType: 'register',
                status: 'failure',
                error: err instanceof Error ? err.message : "Unknown error",
                details: `Failed Firebase registration attempt`,
                ipAddress: err?.clientIp
            });
            throw err;
        }
    };
    // Confirm registration with verification code (Firebase handles this automatically)
    const confirmRegistration = async (email, code) => {
        try {
            setError(null);
            // Firebase handles email verification automatically when user clicks the link
            // This function is kept for compatibility but doesn't need to do anything
            // Log email verification
            await logAuthEvent({
                email: email,
                eventType: 'email_verification',
                status: 'success',
                details: `Email verified for ${email}`,
                ipAddress: undefined
            });
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to confirm registration");
            // Log failed email verification
            await logAuthEvent({
                email: email,
                eventType: 'email_verification',
                status: 'failure',
                error: err instanceof Error ? err.message : "Unknown error",
                details: `Failed email verification for ${email}`,
                ipAddress: err?.clientIp
            });
            throw err;
        }
    };
    // Login an existing user using Firebase
    const login = async (email, password) => {
        try {
            setError(null);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            if (user) {
                // Format user with role information
                const formattedUser = await formatUser(user);
                setCurrentUser(formattedUser);
                // Log successful login
                await logAuthEvent({
                    userId: user.uid || 'unknown',
                    email: email,
                    eventType: 'login_success',
                    status: 'success',
                    details: `User logged in successfully with Firebase`,
                    ipAddress: undefined
                });
                return { isSignedIn: true };
            }
            return { isSignedIn: false };
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to login");
            // Log failed login attempt
            await logAuthEvent({
                email: email,
                eventType: 'login_failure',
                status: 'failure',
                error: err instanceof Error ? err.message : "Unknown error",
                details: `Failed Firebase login attempt`
            });
            throw err;
        }
    };
    // Logout the current user
    const logout = async (reason) => {
        try {
            setError(null);
            // Log logout event before signing out
            if (currentUser) {
                await logAuthEvent({
                    userId: currentUser.uid,
                    email: currentUser.email || "",
                    eventType: 'logout',
                    status: 'success',
                    details: reason || `User logged out`
                });
            }
            await firebaseSignOut(auth);
            setCurrentUser(null);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to logout");
            // Log failed logout
            if (currentUser) {
                await logAuthEvent({
                    userId: currentUser.uid,
                    email: currentUser.email || "",
                    eventType: 'logout',
                    status: 'failure',
                    error: err instanceof Error ? err.message : "Unknown error",
                    details: `Failed logout attempt`
                });
            }
            throw err;
        }
    };
    // Reset password using Firebase
    const requestPasswordReset = async (email) => {
        try {
            setError(null);
            await sendPasswordResetEmail(auth, email);
            // Log password reset request
            await logAuthEvent({
                email: email,
                eventType: 'password_reset_request',
                status: 'success',
                details: `Password reset requested for ${email}`,
                ipAddress: undefined
            });
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to reset password");
            // Log failed password reset request
            await logAuthEvent({
                email: email,
                eventType: 'password_reset_request',
                status: 'failure',
                error: err instanceof Error ? err.message : "Unknown error",
                details: `Failed password reset request for ${email}`,
                ipAddress: err?.clientIp
            });
            throw err;
        }
    };
    // Confirm new password with verification code using Firebase
    const confirmPasswordResetHandler = async (email, code, newPassword) => {
        try {
            setError(null);
            await confirmPasswordReset(auth, code, newPassword);
            // Log successful password reset
            await logAuthEvent({
                email: email,
                eventType: 'password_reset_complete',
                status: 'success',
                details: `Password reset completed for ${email}`,
                ipAddress: undefined
            });
            // Revoke all user sessions after password reset for security
            if (currentUser) {
                await secureAuthService.revokeUserSessions(currentUser.uid);
                console.log(`Revoked all sessions for user ${currentUser.uid} after password reset.`);
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to confirm password reset");
            // Log failed password reset
            await logAuthEvent({
                email: email,
                eventType: 'password_reset_complete',
                status: 'failure',
                error: err instanceof Error ? err.message : "Unknown error",
                details: `Failed password reset completion for ${email}`,
                ipAddress: err?.clientIp
            });
            throw err;
        }
    };
    // Update user profile
    const updateUserProfile = async (profile) => {
        try {
            setError(null);
            const user = auth.currentUser;
            if (!user) {
                throw new Error("No authenticated user");
            }
            // Update user profile
            if (user) {
                await firebaseUpdateProfile(user, {
                    displayName: profile.displayName || null,
                    photoURL: profile.photoURL || null,
                });
            }
            // Refresh current user
            setCurrentUser({
                ...currentUser,
                ...profile,
            });
            // Log profile update
            await logAuthEvent({
                userId: user.uid,
                email: user.email || "",
                eventType: 'profile_update',
                status: 'success',
                details: `User profile updated: ${Object.keys(profile).join(', ')}`
            });
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update profile");
            // Log failed profile update
            if (currentUser) {
                await logAuthEvent({
                    userId: currentUser.uid,
                    email: currentUser.email || "",
                    eventType: 'profile_update',
                    status: 'failure',
                    error: err instanceof Error ? err.message : "Unknown error",
                    details: `Failed profile update: ${Object.keys(profile).join(', ')}`
                });
            }
            throw err;
        }
    };
    // Check if current user is an admin
    const isAdmin = () => {
        return currentUser?.isAdmin || false;
    };
    // Set a user as admin
    const setUserAsAdmin = async (uid) => {
        try {
            setError(null);
            // Only admins can set other users as admin
            if (!isAdmin()) {
                throw new Error("Only admins can set other users as admin");
            }
            // Get user data to log email
            const userData = await getUserData(uid);
            await updateUserRole(uid, UserRole.ADMIN);
            // If the current user is being updated, refresh their data
            if (currentUser && currentUser.uid === uid) {
                const user = auth.currentUser;
                if (user) {
                    const updatedUser = await formatUser(user);
                    setCurrentUser(updatedUser);
                }
            }
            // Log admin role assignment
            await logAuthEvent({
                userId: uid,
                email: userData?.email || "",
                eventType: 'role_change',
                status: 'success',
                details: `User role changed to ADMIN`,
                adminAction: true,
                adminId: currentUser?.uid
            });
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to set user as admin");
            // Log failed admin role assignment
            if (currentUser) {
                await logAuthEvent({
                    userId: uid,
                    email: "", // We don't have the email if it failed
                    eventType: 'role_change',
                    status: 'failure',
                    error: err instanceof Error ? err.message : "Unknown error",
                    details: `Failed to change user role to ADMIN`,
                    adminAction: true,
                    adminId: currentUser.uid
                });
            }
            throw err;
        }
    };
    // Check for current authenticated user on mount
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const formattedUser = await formatUser(firebaseUser);
                    setCurrentUser(formattedUser);
                }
                catch (err) {
                    console.error("Error formatting user:", err);
                    setCurrentUser(null);
                }
            }
            else {
                setCurrentUser(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);
    const value = {
        currentUser,
        loading,
        error,
        login,
        register,
        confirmSignUp: confirmRegistration,
        logout,
        resetPassword: requestPasswordReset,
        confirmResetPassword: confirmPasswordResetHandler,
        updateProfile: updateUserProfile,
        isAdmin,
        setUserAsAdmin,
    };
    return (React.createElement(AuthContext.Provider, { value: value }, !loading && children));
};
export default AuthContext;
